import { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SEO from '@/components/common/SEO';
import MapleMarketInfoView, { type MapleMarketInfoPageId } from '@/components/maple-market/MapleMarketInfoView';
import MapleMarketStoreView from '@/components/maple-market/MapleMarketStoreView';
import type { MapleMarketCategory } from '@/data/mapleMarketApps';

export default function MapleMarket() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const root = document.documentElement;
    root.classList.add('allow-window-scroll');
    return () => {
      root.classList.remove('allow-window-scroll');
    };
  }, []);

  const activeCategory = useMemo(() => {
    const category = searchParams.get('category');
    return (category as MapleMarketCategory | null) || 'All';
  }, [searchParams]);

  const activePage = useMemo(() => {
    const page = searchParams.get('page');
    return page as MapleMarketInfoPageId | null;
  }, [searchParams]);

  const handleCategoryChange = (category: MapleMarketCategory | 'All') => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('page');

    if (category === 'All') {
      nextParams.delete('category');
    } else {
      nextParams.set('category', category);
    }

    setSearchParams(nextParams);
  };

  const handleOpenPage = (page: MapleMarketInfoPageId) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('category');
    nextParams.set('page', page);
    setSearchParams(nextParams);
  };

  const handleBackToStore = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('page');
    setSearchParams(nextParams);
  };

  return (
    <>
      <SEO
        title="Maple Market"
        description="Discover games, apps, creative tools, PWAs, and WASM experiences for MapleOS in Maple Market."
      />
      {activePage ? (
        <MapleMarketInfoView
          page={activePage}
          onBackToStore={handleBackToStore}
          onOpenPage={handleOpenPage}
          onSelectCategory={handleCategoryChange}
          onSelectApp={(appId) => navigate(`/market/app/${appId}`)}
        />
      ) : (
        <MapleMarketStoreView
          activeCategory={activeCategory}
          onCategoryChange={handleCategoryChange}
          onOpenPage={handleOpenPage}
          onSelectApp={(appId) => navigate(`/market/app/${appId}`)}
        />
      )}
    </>
  );
}